import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import KeyIcon from "@mui/icons-material/Key";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { BYOK_PROVIDERS } from "../../../utils/byokProviders";
import useAgencyIsAppSumo from "../../../hooks/useAgencyIsAppSumo";
import { useForm } from "react-hook-form";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { startSaveByokConfiguration } from "../../../actions/byok";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import TextField from "../../../components/Form/TextField";
import Select, {
  SELECT_MENU_ABOVE_MODAL_PROPS,
} from "../../../components/Form/Select";
import Button from "../../../components/Buttons/Button";
import Loader from "../../../components/Loaders/Loader";
import {
  INPUT_COLOR_BORDER,
  BOX_SHADOW_FIELDS,
  GRADIENT_BLACK_COLOR,
} from "../../../utils/colors";
import { BORDER_RADIUS } from "../../../utils/constants";
import {
  API_KEYS_TAB_TITLE,
  BYOK_DEFAULT_PAYMENT_MODE_DESCRIPTION,
  BYOK_DEFAULT_PAYMENT_MODE_LABEL,
  BYOK_KEY_CONFIGURED_STATUS,
  BYOK_KEY_ENCRYPTED_NOTICE,
  BYOK_NO_KEY_CONFIGURED_STATUS,
  BYOK_PAYMENT_MODE_BYOK,
  BYOK_PAYMENT_MODE_CREDITS,
  BYOK_PROVIDER_INPUT_PLACEHOLDER,
  BYOK_PROVIDER_KEYS_TITLE,
  BYOK_PROVIDERS_COUNTER,
  BYOK_REPLACE_KEY,
  BYOK_DELETE_KEY,
  BYOK_SAVE_BUTTON,
  BYOK_SAVE_ERROR,
  BYOK_SAVE_SUCCESS_DESCRIPTION,
  BYOK_SAVE_SUCCESS_TITLE,
  BYOK_SETTINGS_DESCRIPTION,
  BUTTON_CANCEL,
  ERROR,
} from "../../../i18n/keysTranslations";

const PROVIDERS = BYOK_PROVIDERS;
const BYOK_MODAL_MENU_Z_INDEX = 10051;

const PAYMENT_MODE_OPTIONS = [
  { value: "credits", label: BYOK_PAYMENT_MODE_CREDITS },
  { value: "byok", label: BYOK_PAYMENT_MODE_BYOK },
];

const OPTIONAL_FIELD_RULES = { required: false };

const SectionCard = ({ children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${INPUT_COLOR_BORDER}`,
      borderRadius: `${BORDER_RADIUS * 2}px`,
      backgroundColor: "background.paper",
      boxShadow: BOX_SHADOW_FIELDS,
      p: { xs: 2, sm: 2.5 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SectionHeader = ({ icon, title, description }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: `${BORDER_RADIUS * 2}px`,
        background: GRADIENT_BLACK_COLOR,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

const ProviderLogo = ({ provider }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: `${BORDER_RADIUS}px`,
      border: `1px solid ${INPUT_COLOR_BORDER}`,
      backgroundColor: "background.paper",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      overflow: "hidden",
    }}
  >
    <Box
      component="img"
      src={provider.logo}
      alt={provider.label}
      sx={{
        width: 22,
        height: 22,
        objectFit: "contain",
      }}
    />
  </Box>
);

const StatusPill = ({ configured, t }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: configured ? "#22c55e" : "text.disabled",
      }}
    />
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {t(
        configured ? BYOK_KEY_CONFIGURED_STATUS : BYOK_NO_KEY_CONFIGURED_STATUS
      )}
    </Typography>
  </Box>
);

const ProviderActionLink = ({ icon, label, onClick, isDisabled }) => (
  <Typography
    variant="body2"
    onClick={isDisabled ? undefined : onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      fontSize: 13,
      fontWeight: 500,
      color: "text.secondary",
      cursor: isDisabled ? "default" : "pointer",
      opacity: isDisabled ? 0.5 : 1,
      "&:hover": isDisabled ? undefined : { color: "text.primary" },
    }}
  >
    {icon}
    {label}
  </Typography>
);

const ProviderCard = ({
  provider,
  preview,
  control,
  errors,
  isDisabled,
  isReplacing,
  onStartReplace,
  onCancelReplace,
  onDelete,
  t,
}) => {
  const configured = Boolean(preview);
  const showInput = !configured || isReplacing;

  return (
    <Box
      sx={{
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderRadius: `${BORDER_RADIUS * 2}px`,
        backgroundColor: "background.paper",
        boxShadow: BOX_SHADOW_FIELDS,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <ProviderLogo provider={provider} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, lineHeight: 1.2 }}
          >
            {provider.label}
          </Typography>
          <StatusPill configured={configured} t={t} />
        </Box>
      </Box>

      {showInput ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              control={control}
              name={provider.id}
              placeholder={t(BYOK_PROVIDER_INPUT_PLACEHOLDER)}
              rules={OPTIONAL_FIELD_RULES}
              errors={errors}
              fullWidth
              variant="filled"
              disabled={isDisabled}
            />
          </Box>
          {isReplacing && (
            <Button
              type="button"
              color="secondary"
              onClick={() => onCancelReplace(provider.id)}
              disabled={isDisabled}
            >
              {t(BUTTON_CANCEL)}
            </Button>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1.5,
            height: 40,
            borderRadius: `${BORDER_RADIUS}px`,
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            backgroundColor: "background.paper",
            boxShadow: BOX_SHADOW_FIELDS,
            fontFamily:
              "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace",
            fontSize: 13,
            color: "text.primary",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {preview}
        </Box>
      )}

      {!showInput ? (
        <Box
          sx={{
            alignSelf: "flex-start",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mt: 0.25,
          }}
        >
          <ProviderActionLink
            icon={<AddIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
            label={t(BYOK_REPLACE_KEY)}
            onClick={() => onStartReplace(provider.id)}
            isDisabled={isDisabled}
          />
          <ProviderActionLink
            icon={
              <DeleteOutlineIcon
                sx={{ fontSize: 16, color: "text.secondary" }}
              />
            }
            label={t(BYOK_DELETE_KEY)}
            onClick={() => onDelete(provider.id)}
            isDisabled={isDisabled}
          />
        </Box>
      ) : (
        !isReplacing && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t(BYOK_KEY_ENCRYPTED_NOTICE)}
          </Typography>
        )
      )}
    </Box>
  );
};

const ApiKeys = ({ variant = "page", onSaveSuccess } = {}) => {
  const isModal = variant === "modal";
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const { byokConfigurationPublic, isLoadingByokConfiguration } =
    useSelectorApp((state) => state.payments);

  const defaultProviderValues = useMemo(
    () =>
      PROVIDERS.reduce((acc, p) => {
        acc[p.id] = "";
        return acc;
      }, {}),
    []
  );

  const {
    control,
    watch,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      defaultPaymentMode: "credits",
      ...defaultProviderValues,
    },
  });

  const [isSaving, setIsSaving] = useState(false);
  const [replacingProviders, setReplacingProviders] = useState({});
  const [removedProviders, setRemovedProviders] = useState({});

  useEffect(() => {
    reset({
      defaultPaymentMode:
        byokConfigurationPublic?.defaultPaymentMode || "credits",
      ...defaultProviderValues,
    });
    setReplacingProviders({});
    setRemovedProviders({});
  }, [byokConfigurationPublic, reset, defaultProviderValues]);

  const formValues = watch();
  const savedPreviews = useMemo(
    () => byokConfigurationPublic?.providers || {},
    [byokConfigurationPublic?.providers]
  );
  const savedPaymentMode =
    byokConfigurationPublic?.defaultPaymentMode || "credits";

  const effectivePreviews = useMemo(() => {
    const next = { ...savedPreviews };
    PROVIDERS.forEach((provider) => {
      if (removedProviders[provider.id]) {
        delete next[provider.id];
      }
    });
    return next;
  }, [savedPreviews, removedProviders]);

  const configuredCount = PROVIDERS.reduce(
    (acc, p) => (effectivePreviews[p.id] ? acc + 1 : acc),
    0
  );

  const hasPendingChanges = useMemo(() => {
    const hasProviderDraft = PROVIDERS.some((provider) =>
      Boolean(String(formValues[provider.id] || "").trim())
    );
    const hasPaymentModeChange =
      formValues.defaultPaymentMode !== savedPaymentMode;
    const hasProviderRemoval = PROVIDERS.some(
      (provider) =>
        removedProviders[provider.id] && Boolean(savedPreviews[provider.id])
    );

    return hasProviderDraft || hasPaymentModeChange || hasProviderRemoval;
  }, [formValues, savedPaymentMode, removedProviders, savedPreviews]);

  const isDisabled = isSaving || isLoadingByokConfiguration;

  const handleStartReplace = (providerId) => {
    setReplacingProviders((prev) => ({ ...prev, [providerId]: true }));
  };

  const handleCancelReplace = (providerId) => {
    setReplacingProviders((prev) => {
      const next = { ...prev };
      delete next[providerId];
      return next;
    });
    reset({ ...getValues(), [providerId]: "" });
  };

  const handleDeleteProvider = (providerId) => {
    setRemovedProviders((prev) => ({ ...prev, [providerId]: true }));
    setReplacingProviders((prev) => {
      if (!prev[providerId]) return prev;
      const next = { ...prev };
      delete next[providerId];
      return next;
    });
    reset({ ...getValues(), [providerId]: "" });
  };

  const onSave = async () => {
    const values = getValues();
    const providers = PROVIDERS.reduce((acc, provider) => {
      const value = String(values[provider.id] || "").trim();
      if (value) {
        acc[provider.id] = value;
      }
      return acc;
    }, {});
    const removeProviders = PROVIDERS.filter(
      (provider) =>
        removedProviders[provider.id] &&
        savedPreviews[provider.id] &&
        !providers[provider.id]
    ).map((provider) => provider.id);

    setIsSaving(true);
    const response = await dispatch(
      startSaveByokConfiguration({
        defaultPaymentMode: values.defaultPaymentMode,
        providers,
        removeProviders,
      })
    );
    setIsSaving(false);

    if (!response?.ok) {
      SimpleAlert({
        title: t(ERROR),
        text: t(BYOK_SAVE_ERROR),
        icon: "error",
      });
      return;
    }

    reset({
      defaultPaymentMode: values.defaultPaymentMode,
      ...defaultProviderValues,
    });
    setReplacingProviders({});
    setRemovedProviders({});

    if (isModal) {
      onSaveSuccess?.();
      return;
    }

    SimpleAlert({
      title: t(BYOK_SAVE_SUCCESS_TITLE),
      text: t(BYOK_SAVE_SUCCESS_DESCRIPTION),
      icon: "success",
    });
  };

  if (
    !isAppSumoResolved ||
    (isLoadingByokConfiguration && !byokConfigurationPublic)
  ) {
    return <Loader />;
  }

  if (!isAppSumo) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          pb: isModal ? 0 : { xs: "80px", md: "80px" },
        }}
      >
        {!isModal ? (
          <SectionHeader
            icon={<KeyIcon sx={{ color: "white !important" }} />}
            title={t(API_KEYS_TAB_TITLE)}
            description={t(BYOK_SETTINGS_DESCRIPTION)}
          />
        ) : null}

        <SectionCard>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: `${BORDER_RADIUS}px`,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  flexShrink: 0,
                }}
              >
                <CreditCardIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, lineHeight: 1.3 }}
                >
                  {t(BYOK_DEFAULT_PAYMENT_MODE_LABEL)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block" }}
                >
                  {t(BYOK_DEFAULT_PAYMENT_MODE_DESCRIPTION)}
                </Typography>
              </Box>
            </Box>

            <Select
              control={control}
              name="defaultPaymentMode"
              options={PAYMENT_MODE_OPTIONS}
              doTranslate
              rules={OPTIONAL_FIELD_RULES}
              errors={errors}
              fullWidth
              variant="filled"
              disabled={isDisabled}
              MenuProps={
                isModal
                  ? {
                      ...SELECT_MENU_ABOVE_MODAL_PROPS,
                      style: { zIndex: BYOK_MODAL_MENU_Z_INDEX },
                      PaperProps: {
                        style: {
                          maxHeight: 48 * 4.5 + 8 * 2,
                          zIndex: BYOK_MODAL_MENU_Z_INDEX,
                        },
                      },
                    }
                  : undefined
              }
            />
          </Box>
        </SectionCard>

        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t(BYOK_PROVIDER_KEYS_TITLE)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t(BYOK_PROVIDERS_COUNTER, {
                configured: configuredCount,
                total: PROVIDERS.length,
              })}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {PROVIDERS.map((provider) => (
              <Grid item xs={12} md={6} key={provider.id}>
                <ProviderCard
                  provider={provider}
                  preview={effectivePreviews[provider.id]}
                  control={control}
                  errors={errors}
                  isDisabled={isDisabled}
                  isReplacing={Boolean(replacingProviders[provider.id])}
                  onStartReplace={handleStartReplace}
                  onCancelReplace={handleCancelReplace}
                  onDelete={handleDeleteProvider}
                  t={t}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          p: 2,
          borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
          display: "flex",
          justifyContent: "flex-end",
          ...(isModal
            ? {
                mt: 1,
                backgroundColor: "background.paper",
              }
            : {
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: "background.paper",
                zIndex: 1000,
              }),
        }}
      >
        <Button
          color="primary"
          type="button"
          loading={isSaving}
          disabled={!hasPendingChanges || isDisabled}
          onClick={onSave}
        >
          {t(BYOK_SAVE_BUTTON)}
        </Button>
      </Box>
    </>
  );
};

export default ApiKeys;
